<template>
  <ion-row>
    <ion-col size="12">
      <h4 class="h4 text-align-left mt-8 text-color-placeholder">
        {{ t('profile.account.deleteAccount') }}
      </h4>
    </ion-col>

    <ion-col size="12">
      <div class="d-flex ion-padding-bottom flex-column">
        <p class="font-sm">{{ t('profile.account.description') }}</p>
        <ion-button
          @click="isOpen = true"
          fill="outline"
          expand="block"
          size="default"
          color="error"
          class="text-lg text-weight-bold w-100 mt-xs"
        >
          {{ t('profile.account.deleteAccount') }}
        </ion-button>
      </div>
    </ion-col>

    <ion-modal
      :can-dismiss="canDismiss"
      :initial-breakpoint="0.45"
      :is-open="isOpen"
      @did-dismiss="isOpen = false"
    >
      <swiper ref="changePasswordSwiper" @swiper="onSwiper">
        <swiper-slide class="swiper-no-swiping flex-column">
          <div class="px-lg py-md align-left">
            <h4 class="h4 text-align-left mt-8 text-color-placeholder px-sm">
              {{ t('profile.account.request') }}
            </h4>

            <p class="font-sm mt-sm text-error text-align-left px-sm">
              {{ t('profile.account.requestDescription') }}
            </p>
            <Form
              form-name="delete"
              id="changePasswordForm"
              :validation-schema="schemaSignUpPassword"
              @submit="handleDeleteAccount"
            >
              <ion-row>
                <ion-col size="12" class="ion-no-margin">
                  <div class="mt-32 mb-16">
                    <CustomInput
                      form-name="delete"
                      type="password"
                      :value="password"
                      v-model="password"
                      input-type="password"
                      :label="t('profile.password.confirmPassword')"
                      name="password"
                      class-name="custom-field"
                      :placeholder="t('profile.password.confirmPassword')"
                      :icon="lockClosedOutline"
                      :clear-field="false"
                    />
                  </div>
                </ion-col>
              </ion-row>

              <ion-row class="mt-16">
                <ion-col size="5" class="ion-no-margin">
                  <ion-button
                    expand="block"
                    color="primary"
                    fill="clear"
                    class="text-weight-bold"
                    @click="isOpen = false"
                    >{{ t('actions.cancel') }}
                  </ion-button>
                </ion-col>
                <ion-col size="7" class="ion-no-margin">
                  <ion-button
                    :disabled="isLoading"
                    expand="block"
                    color="error"
                    fill="solid"
                    class="text-weight-bold no-shadow"
                    type="submit"
                    >{{ t('actions.delete') }}
                  </ion-button>
                </ion-col>
              </ion-row>
            </Form>
          </div>
          <ion-progress-bar v-if="isLoading" type="indeterminate"></ion-progress-bar>
        </swiper-slide>
        <swiper-slide class="swiper-no-swiping flex-column">
          <div class="px-lg py-md align-left w-100">
            <h4 class="h4 text-align-left mt-8 text-color-placeholder px-sm">
              {{ t('profile.account.requestInProcess') }}
            </h4>

            <p class="font-sm mt-sm text-align-left px-sm">
              {{ t('profile.account.dateProgramed') }}
              <b>{{ formatDateTime(finalDate, locale, true, true) }}</b
              >{{ t('profile.account.cancel') }}
            </p>

            <ion-button
              @click="handleLogout"
              expand="block"
              color="primary"
              fill="solid"
              class="text-weight-bold no-shadow mt-lg"
              type="submit"
              >{{ t('actions.confirm') }}
            </ion-button>
          </div>
        </swiper-slide>
      </swiper>
    </ion-modal>
  </ion-row>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { IonModal, IonRow, IonCol, IonButton, IonProgressBar } from '@ionic/vue';
  import { useI18n } from 'vue-i18n';
  import CustomInput from '@/components/shared/form/CustomInput.vue';
  import { Form } from 'vee-validate';
  import { useSchemas } from '@/composables/useSchemas';
  import { lockClosedOutline } from 'ionicons/icons';
  import { useProfile } from '@/composables/useProfile';
  import { Swiper, SwiperSlide } from 'swiper/vue';
  import { formatDateTime } from '@/utils/date-utils';
  import { useAuthStore } from '@/stores/auth-store';
  import { storeToRefs } from 'pinia';

  const { schemaSignUpPassword } = useSchemas();
  const { handleRemoveAccount } = useProfile();
  const { resetStore } = useAuthStore();
  const { t, locale } = useI18n();
  const { user, token } = storeToRefs(useAuthStore());

  const isOpen = ref(false);
  const isLoading = ref(false);
  const password = ref('');
  const canDismiss = ref(true);

  const swiperInstance = ref();
  const selectedSwiper = ref(0);
  const currentStep = ref(0);

  const finalDate = ref('');

  const handleLogout = async () => {
    canDismiss.value = true;
    isOpen.value = false;
    await resetStore();
    window.location.reload();
  };

  const onSwiper = (swiper: any) => {
    swiperInstance.value = swiper;
    swiperInstance.value?.slideTo(selectedSwiper.value);
  };

  const handleSlideTo = (idx: number) => {
    if (idx >= 0) {
      selectedSwiper.value = idx;
      swiperInstance.value?.slideTo(selectedSwiper.value);
      currentStep.value = idx;
    }
  };

  const removeSession = () => {
    token.value = '';
    user.value = undefined;
    localStorage.clear();
  };

  const handleDeleteAccount = async () => {
    isLoading.value = true;
    const data = await handleRemoveAccount(password.value);
    if (data) {
      finalDate.value = data.dateDelete;
      handleSlideTo(1);
      removeSession();
      canDismiss.value = false;
    }

    isLoading.value = false;
  };
</script>

<style scoped lang="scss">
  .swiper {
    width: 100% !important;
  }

  ion-button {
    height: 36px;
  }

  ion-modal {
    --border-radius: 25px;
    --background: white;
  }

  .text-error {
    color: var(--ion-error);
    font-weight: 600;
  }

  .align-left {
    align-items: start;
  }
</style>
